import { CourseLanguage, HourEarnedType, LanguageType, UiProgramType } from '../enum/ui-utils.enum';
import { SpeakerDetails } from './speaker.model';
import { CourseActionType, CourseType, ProgramFilter, ProgramType, WebinarType } from '../enum/webinar-course.enum';
import { ListFilter } from './filter.model';
import { SearchHighlights } from './search-highlight.model';
import { getProgramMainRoute } from '../components/webinar-course/webinar-course-helper';
import { getLongLanguageOptions, Language } from '../constants/ui-utils.constant';
import { MINUTES_BEFORE_WEBINAR_START } from '../constants/webinar-course.constant';
import { NON_BREAKING_SPACES } from '../constants/app.constant';

export interface FeaturedBanner {
    about: string;
    certificateAttachmentUrl: string;
    completedAt: string;
    description: string;
    duration: number;
    endDate: string;
    id: string;
    isEnrol: boolean;
    isFavorite: boolean;
    isFeatured: boolean;
    isMeetingEnded: boolean;
    joinUrl?: string;
    language: LanguageType;
    programCategory: string[];
    programImageUrl: string;
    programType: ProgramType;
    progress: number;
    recordedVideoUrl: string;
    speakerHeaderImageUrl?: string;
    speakerName?: string;
    speakerPosition?: string;
    startDate: string;
    title: string;
    willIncludeRecording: boolean;
    webinarType: WebinarType;
}

export class FeaturedBannerModel {
    about: string;
    certificateAttachmentUrl: string;
    completedAt: string;
    description: string;
    duration: number;
    endDate: string;
    id: string;
    isEnrol: boolean;
    isFavorite: boolean;
    isFeatured: boolean;
    isMeetingEnded: boolean;
    isProgramExpired: boolean;
    isProgramJoinable: boolean;
    joinUrl?: string;
    language: LanguageType;
    programCategory: string[];
    programImageUrl: string;
    programRoute: string;
    programType: ProgramType;
    progress: number;
    recordedVideoUrl: string;
    speakerHeaderImageUrl?: string;
    speakerName?: string;
    speakerPosition?: string;
    startDate: string;
    title: string;
    uiProgramType: UiProgramType;
    webinarType: WebinarType;
    willIncludeRecording: boolean;

    constructor(model: FeaturedBanner) {
        this.about = model.about;
        this.certificateAttachmentUrl = model.certificateAttachmentUrl;
        this.completedAt = model.completedAt;
        this.description = model.description;
        this.duration = model.duration;
        this.endDate = model.endDate;
        this.id = model.id;
        this.isEnrol = model.isEnrol;
        this.isFavorite = model.isFavorite;
        this.isFeatured = model.isFeatured;
        this.isMeetingEnded = model.isMeetingEnded;
        this.joinUrl = model.joinUrl;
        this.language = model.language;
        this.programCategory = model.programCategory;
        this.programImageUrl = model.programImageUrl;
        this.programType = model.programType;
        this.progress = model.progress;
        this.recordedVideoUrl = model.recordedVideoUrl;
        this.speakerHeaderImageUrl = model.speakerHeaderImageUrl;
        this.speakerName = model.speakerName;
        this.speakerPosition = model.speakerPosition;
        this.startDate = model.startDate;
        this.title = model.title.replace(/ {2,}/g, (match) => {
            return ' ' + NON_BREAKING_SPACES.repeat(match.length - 1);
        });
        this.webinarType = model.webinarType;
        this.willIncludeRecording = model.willIncludeRecording;
        this.uiProgramType = getUiProgramType(this.programType, model.webinarType);
        this.isProgramExpired = checkIsProgramExpired(new Date(this.endDate), model.isMeetingEnded);
        this.isProgramJoinable = checkIfProgramIsJoinable(new Date(this.startDate), new Date(this.endDate), model.isMeetingEnded);
        this.programRoute = getProgramMainRoute(getUiProgramType(this.programType, model.webinarType));
    }
}

export interface WebinarSchedule {
    id: number;
    imageUrl: string;
    language: CourseLanguage;
    eventDate: string;
    startTime: string;
    endTime: string;
    title: string;
    description: string;
    type: HourEarnedType;
    duration: string;
    link: string;
}

export interface CourseDetail {
    id: number;
    imageUrl: string;
    title: string;
    description: string;
    type: CourseType;
    duration: string;
    link: string;
    actionType: CourseActionType;
}

export interface ProgramsFilterInterface extends ListFilter {
    programType?: ProgramType[];
    webinarType?: WebinarType[];
    programTopics?: string[];
    programFilter?: ProgramFilter;
    language?: Language[];
    orderBy?: string;
    isOrderByAsc?: boolean;
    isFeatured?: boolean | null;
    isCalendar?: boolean;
}

export class ProgramsFilterModel {
    programType: ProgramType[] = [];
    webinarType: WebinarType[] = [];
    programTopics: string[] = [];
    language: Language[] = [];
    programFilter: ProgramFilter | null;
    skip: number;
    take: number;
    paging: boolean;
    startDate?: string;
    endDate?: string;
    searchBy?: string;
    searchString?: string;
    orderBy?: string;
    isOrderByAsc?: boolean;
    isFeatured?: boolean | null;
    isCalendar?: boolean;

    constructor(private model: ProgramsFilterInterface) {
        this.programType = model.programType || [];
        this.webinarType = model.webinarType || [];
        this.programTopics = model.programTopics || [];
        this.language = model.language || [];
        this.programFilter = model.programFilter || null;
        this.skip = model.skip || 0;
        this.take = model.take || 9;
        this.paging = model.paging || true;
        this.startDate = model.startDate;
        this.endDate = model.endDate;
        this.searchBy = model.searchBy;
        this.searchString = model.searchString;
        this.orderBy = model.orderBy;
        this.isOrderByAsc = model.isOrderByAsc;
        this.isFeatured = model.isFeatured;
        this.isCalendar = model.isCalendar ?? false;
    }
}

//TODO: Replace this with the proper model, currently is use for dummy data
export interface CourseListingDetail {
    id: number;
    imageUrl: string;
    language: CourseLanguage;
    eventDate: string;
    startTime: string;
    endTime: string;
    title: string;
    type: HourEarnedType;
    duration: string;
    link: string;
    speaker: string;
    speakerTitle: string;
}

export interface CourseListingDetailInterface {
    about: string;
    attachmentId: string;
    attachmentUrl: string;
    category: string[];
    certificateAttachmentId: number;
    certificateAttachmentUrl: string;
    completedAt: string;
    creditHours: number;
    duration: number;
    endDate: string;
    id: string;
    isEnrol: boolean;
    isFavorite: boolean;
    isFeatured: boolean;
    isInProgress: boolean;
    isMeetingEnded: boolean;
    joinUrl: string;
    language: Language;
    programType: ProgramType;
    progress: number;
    publishedDate: string;
    speakerId: string;
    speakerName: string;
    speakerPosition: string;
    startDate: string;
    title: string;
    videoRecordedUrl: string;
    webinarType: WebinarType;
    willIncludeRecording: boolean;
    speakerHeaderImageUrl?: string;
    isCertificateEnabled?: boolean;
    categoryTranslations?: CategoryTranslations[];
    /** Optional highlights returned from search API */
    highlights?: SearchHighlights;
    /** Array of matched search tokens */
    matchedTokens?: string[];
}

export class CourseListingDetailModel {
    about: string;
    attachmentId: string;
    attachmentUrl: string;
    category: string[];
    certificateAttachmentId: number;
    certificateAttachmentUrl: string;
    completedAt: string;
    creditHours: number;
    duration: number;
    endDate: string;
    id: string;
    isEnrol: boolean;
    isFavorite: boolean;
    isFeatured: boolean;
    isInProgress: boolean;
    isMeetingEnded: boolean;
    isProgramExpired: boolean;
    isProgramJoinable: boolean;
    joinUrl: string;
    language: Language;
    liveDaysCountdown: number;
    liveSecondsCountdown: number;
    programRoute: string;
    programType: ProgramType;
    progress: number;
    publishedDate: string;
    speakerId: string;
    speakerName: string;
    speakerPosition: string;
    startDate: string;
    title: string;
    uiProgramType: UiProgramType;
    videoRecordedUrl: string;
    webinarType: WebinarType;
    willIncludeRecording: boolean;
    speakerHeaderImageUrl?: string;
    isCertificateEnabled?: boolean;
    categoryTranslations?: CategoryTranslations[];
    /** Optional highlights returned from search API */
    highlights?: SearchHighlights;
    /** Array of matched search tokens */
    matchedTokens?: string[];

    constructor(
        private model: CourseListingDetailInterface,
        private isAllEnrolled = false,
    ) {
        this.about = model.about;
        this.attachmentId = model.attachmentId;
        this.attachmentUrl = model.attachmentUrl;
        this.category = model.category;
        this.duration = model.duration;
        this.endDate = model.endDate;
        this.id = model.id;
        this.isEnrol = isAllEnrolled ? true : model.isEnrol;
        this.isFavorite = model.isFavorite;
        this.isFeatured = model.isFeatured;
        this.isInProgress = model.isInProgress;
        this.joinUrl = model.joinUrl;
        this.language = model.language;
        this.programType = model.programType;
        this.publishedDate = model.publishedDate;
        this.speakerId = model.speakerId;
        this.speakerName = model.speakerName;
        this.speakerPosition = model.speakerPosition;
        this.startDate = model.startDate;
        this.title = model.title.replace(/ {2,}/g, (match) => {
            return ' ' + NON_BREAKING_SPACES.repeat(match.length - 1);
        });
        this.videoRecordedUrl = '';
        this.liveDaysCountdown = calculateLiveDaysCountdown(new Date(this.startDate));
        this.liveSecondsCountdown = calculateLiveSecondsCountdown(new Date(this.startDate));
        this.isProgramExpired = checkIsProgramExpired(new Date(this.endDate),model.isMeetingEnded);
        this.isProgramJoinable = checkIfProgramIsJoinable(new Date(this.startDate), new Date(this.endDate), model.isMeetingEnded);
        this.uiProgramType = getUiProgramType(this.programType, model.webinarType);
        this.isMeetingEnded = model.isMeetingEnded;
        this.programRoute = getProgramMainRoute(getUiProgramType(this.programType, model.webinarType));
        this.creditHours = model.creditHours || 0;
        this.webinarType = model.webinarType;
        this.progress = model.progress || 0;
        this.completedAt = model.completedAt || '';
        this.certificateAttachmentId = model.certificateAttachmentId || 0;
        this.certificateAttachmentUrl = model.certificateAttachmentUrl || '';
        this.willIncludeRecording = model.willIncludeRecording ?? false;
        this.speakerHeaderImageUrl = model.speakerHeaderImageUrl;
        this.isCertificateEnabled = model.isCertificateEnabled;
        this.categoryTranslations = model.categoryTranslations;
        this.highlights = model.highlights;
        this.matchedTokens = model.matchedTokens;
    }
}

export interface CategoryTranslations {
    language: Language;
    description: string[];
}

export interface SimpleProgramDetails {
    id: string;
    title: string;
    imageUrl: string;
    programType: ProgramType;
    startDate: string;
    endDate: string;
    isFavorite: boolean;
    isEnrol: boolean;
    creditHours: number;
}

export interface ProgramParamBody {
    language?: string | null;
    topic?: string | null;
    programType?: string | null;
    webinarType?: string | null;
}

export interface ProgramDetails extends SimpleProgramDetails {
    about: string;
    attachmentId?: string;
    attachmentUrl?: string;
    allowedDownload: boolean;
    availableLanguage: AvailableLanguageInterface[];
    certificateAttachmentId: number;
    certificateAttachmentUrl: string;
    completedAt: string;
    description: string;
    duration: number;
    isFeatured: boolean;
    isInProgress: boolean;
    isMeetingEnded: boolean;
    joinUrl?: string;
    language: Language;
    programCategory?: string[];
    programChapters: ProgramChapter[];
    programImageUrl?: string;
    progress: number;
    publishedDate?: string;
    recordedVideoUrl: string;
    videoHash?: string;
    speakerDetails: SpeakerDetails;
    speakerHeaderImageUrl?: string;
    speakerId: string;
    speakerName?: string;
    speakerPosition?: string;
    webinarType: WebinarType;
    willIncludeRecording: boolean;
    isCertificateEnabled?: boolean;
    categoryTranslations?: CategoryTranslations[];
}

export class ProgramDetailsModel {
    about: string;
    attachmentId?: string;
    attachmentUrl?: string;
    allowedDownload: boolean;
    availableLanguage: AvailableLanguageInterface[];
    completedAt: string;
    certificateAttachmentId: number;
    certificateAttachmentUrl: string;
    description: string;
    duration: number;
    endDate: string;
    id: string;
    imageUrl: string;
    isEnrol: boolean;
    isFavorite: boolean;
    isFeatured: boolean;
    isInProgress: boolean;
    isMeetingEnded: boolean;
    joinUrl?: string;
    language: Language;
    liveDaysCountdown: number;
    liveSecondsCountdown: number;
    programCategory?: string[];
    programChapters: ProgramChapter[];
    programImageUrl?: string;
    programType: ProgramType;
    progress: number;
    publishedDate?: string;
    recordedVideoUrl: string;
    speakerId: string;
    speakerDetails: SpeakerDetails;
    speakerHeaderImageUrl?: string;
    speakerName?: string;
    speakerPosition?: string;
    startDate: string;
    title: string;
    webinarType: WebinarType;
    isProgramExpired: boolean;
    isProgramJoinable: boolean;
    uiProgramType: UiProgramType;
    programRoute: string;
    creditHours: number;
    videoHash?: string;
    willIncludeRecording: boolean;
    isCertificateEnabled?: boolean;
    categoryTranslations?: CategoryTranslations[];

    constructor(private model: ProgramDetails) {
        this.about = model.about;
        this.attachmentId = model.attachmentId;
        this.attachmentUrl = model.attachmentUrl;
        this.allowedDownload = model.allowedDownload || false;
        this.availableLanguage = model.availableLanguage;
        this.completedAt = model.completedAt;
        this.certificateAttachmentId = model.certificateAttachmentId;
        this.certificateAttachmentUrl = model.certificateAttachmentUrl;
        this.description = model.description;
        this.duration = model.duration;
        this.endDate = model.endDate;
        this.id = model.id;
        this.imageUrl = model.imageUrl;
        this.isEnrol = model.isEnrol;
        this.isFavorite = model.isFavorite;
        this.isFeatured = model.isFeatured;
        this.isInProgress = model.isInProgress;
        this.isMeetingEnded = model.isMeetingEnded;
        this.joinUrl = model.joinUrl;
        this.language = model.language;
        this.programCategory = model.programCategory;
        this.programChapters = model.programChapters;
        this.programImageUrl = model.programImageUrl;
        this.programType = model.programType;
        this.publishedDate = model.publishedDate;
        this.recordedVideoUrl = model.recordedVideoUrl;
        this.videoHash = model.videoHash;
        this.speakerId = model.speakerId;
        this.speakerDetails = model.speakerDetails;
        this.speakerHeaderImageUrl = model.speakerHeaderImageUrl;
        this.speakerName = model.speakerName;
        this.speakerPosition = model.speakerPosition;
        this.startDate = model.startDate;
        this.webinarType = model.webinarType;
        this.title = model.title.replace(/ {2,}/g, (match) => {
            return ' ' + NON_BREAKING_SPACES.repeat(match.length - 1);
        });
        this.liveDaysCountdown = calculateLiveDaysCountdown(new Date(this.startDate));
        this.liveSecondsCountdown = calculateLiveSecondsCountdown(new Date(this.startDate));
        this.isProgramExpired = checkIsProgramExpired(new Date(this.endDate), model.isMeetingEnded);
        this.isProgramJoinable = checkIfProgramIsJoinable(new Date(this.startDate), new Date(this.endDate), model.isMeetingEnded);
        this.uiProgramType = getUiProgramType(this.programType, model.webinarType);
        this.programRoute = getProgramMainRoute(getUiProgramType(this.programType, model.webinarType));
        this.progress = model.progress || 0;
        this.creditHours = model.creditHours;
        this.willIncludeRecording = model.willIncludeRecording;
        this.isCertificateEnabled = model.isCertificateEnabled;
        this.categoryTranslations = model.categoryTranslations;
    }
}

export interface ProgramChapter {
    title: string;
    description: string;
    details: ProgramChapterDetails[];
}

export interface ProgramChapterDetails {
    title: string;
    description: string;
    duration: number;
    videoUrl: string;
    classMaterials: ClassMaterials[];
}

export interface ClassMaterials {
    id: string;
    name: string;
    attachmentUrl: string;
}

export interface CourseListSimpleFilter extends ListFilter {
    programType: ProgramType[];
    programFilter: ProgramFilter;
}

export interface ProgramDetailsInterface {
    id: string;
    programType: ProgramType;
    title: string;
    about: string;
    joinUrl: string;
    recordedVideoUrl: string;
    startDate: string;
    endDate: string;
    description: string;
    isFeatured: boolean;
    duration: number;
    isFavourite: boolean;
    isEnrol: boolean;
    speakerHeaderImageUrl: string;
    speakerName: string;
    speakerPosition: string;
    language: LanguageType;
    programCategory: string[];
    programImageUrl: string;
}

export interface ChapterKeyTakeaway {
    title: string;
    content: string;
    startTime: string;
    endTime: string;
}

export interface ProgramChapterDetailsInterface {
    id: string;
    title: string;
    parentId: string;
    sequence: number;
    description: string;
    isVideoEnabled: boolean;
    videoUrl: string;
    videoHash?: string;
    duration: number;
    attachmentId: number;
    attachmentUri: string;
    startLearning: string;
    completedLearning: string;
    totalSecondsView: number;
    aiSummaryOverview?: string;
    overviewStartTime?: string;
    overviewEndTime?: string;
    keyTakeaways?: ChapterKeyTakeaway[];
    conclusionSummary?: string;
    conclusionStartTime?: string;
    conclusionEndTime?: string;
}

export interface ProgramChapterListingInterface {
    id: string;
    title: string;
    programChapterDetails: ProgramChapterDetailsInterface[];
}

export class ProgramChapterListingModel {
    id: string;
    title: string;
    programChapterDetails: ProgramChapterDetailsInterface[];
    totalVideoPlayTime: number;
    totalCompleted: number;
    totalCourses: number;

    constructor(private model: ProgramChapterListingInterface) {
        this.id = model.id;
        this.title = model.title.replace(/ {2,}/g, (match) => {
            return ' ' + NON_BREAKING_SPACES.repeat(match.length - 1);
        });
        this.programChapterDetails = model.programChapterDetails;
        this.totalVideoPlayTime = this.calculateTotalVideoPlayTime(model);
        this.totalCourses = this.getTotalCourses();
        this.totalCompleted = this.getTotalCompletedCourses();
    }

    private calculateTotalVideoPlayTime(data: ProgramChapterListingInterface): number {
        return data.programChapterDetails.reduce((total, item) => total + Number(item.duration), 0);
    }

    private getTotalCourses(): number {
        return this.programChapterDetails.filter((item) => item.isVideoEnabled).length;
    }

    private getTotalCompletedCourses(): number {
        return this.programChapterDetails.filter((item) => item.isVideoEnabled && item.completedLearning).length;
    }
}

export interface UpdateProgressInterface {
    programId: string;
    programChapterId?: string;
    startLearning?: string;
    completedLearning?: string;
    totalSecondsView: number;
    isRecordedWebinar: boolean;
    videoTotalSeconds: number;
    isUpdateProgress: boolean;
}

export class LiveWebinarListingUiModel {
    data: LiveWebinarListingUiInterface[] = [];

    constructor(private model: CourseListingDetailModel[]) {
        this.model.forEach((item) => {
            const date = new Date(item.startDate);
            const today = new Date();

            const dateTitle =
                date.getDate() +
                ' ' +
                date.toLocaleString('default', { month: 'short' }) +
                ' ' +
                date.getFullYear() +
                ', ' +
                new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);

            const filteredData = this.data.find((d) => d.dateTitle === dateTitle);

            if (filteredData) {
                filteredData.data.push(item);
            } else {
                this.data.push({
                    dateTitle: dateTitle,
                    data: [item],
                    isToday:
                        date.getDate() === today.getDate() &&
                        date.getMonth() === today.getMonth() &&
                        date.getFullYear() === today.getFullYear(),
                });
            }
        });

        this.sortByDate();
    }

    private sortByDate(): void {
        this.data.sort((a, b) => {
            const dateA = new Date(a.data[0].startDate).getTime();
            const dateB = new Date(b.data[0].startDate).getTime();
            return dateA - dateB;
        });
    }
}

export interface LiveWebinarListingUiInterface {
    data: CourseListingDetailModel[];
    dateTitle: string;
    isToday: boolean;
}

export class ProgramAvailableLanguageToDropdownModel {
    value: string;
    name: string;

    constructor(language: Language, programId: string) {
        this.value = programId;
        this.name = getLongLanguageOptions(language).label;
    }
}

export interface AvailableLanguageInterface {
    language: Language;
    programId: string;
    programName: string;
}

export function getUiProgramType(programType: ProgramType, webinarType: WebinarType): UiProgramType {
    if (programType === ProgramType.Course) {
        return UiProgramType.Course;
    }
    if (programType === ProgramType.Podcast) {
        return UiProgramType.Podcast;
    }

    const webinarTypeUi = webinarType === WebinarType.Live ? UiProgramType.LiveWebinar : UiProgramType.RecordedWebinar;

    return webinarTypeUi;
}

export function calculateLiveDaysCountdown(startDate: Date): number {
    const now = new Date();
    const diffTime = startDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
}

export function calculateLiveSecondsCountdown(startDate: Date): number {
    const now = new Date();
    const diffTime = startDate.getTime() - now.getTime();
    const diffSeconds = Math.floor(diffTime / 1000);

    return diffSeconds;
}

export function checkIfProgramIsJoinable(startDate: Date, endDate: Date, isMeetingEnded:boolean): boolean {
    const now = new Date();
    const adjustedStartDate = new Date(startDate.getTime() - MINUTES_BEFORE_WEBINAR_START);

    return now >= adjustedStartDate && (!isMeetingEnded || (isMeetingEnded && now <= new Date(startDate)));
}

export function checkIsProgramExpired(endDate: Date, isMeetingEnded:boolean): boolean {
    const now = new Date();

    if (!endDate) {
        return false;
    }

    return now > endDate && isMeetingEnded;
}

export function checkIfProgramIsLive(startDate: Date, endDate: Date): boolean {
    const now = new Date();
    const adjustedStartDate = new Date(startDate.getTime());

    return now >= adjustedStartDate && now < endDate;
}

export interface RecommendedProgramsResponse {
    programs: CourseListingDetailInterface[];
}
